import { GlobalService } from "@app/shared/services/global/global.service";
import {
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { tap, catchError, map } from "rxjs/operators";
import { ToastrMessageService } from "@app/shared/services/toastr-message/toastr-message.service";
import { LocalStorageService } from "@app/shared/services/local-storage/local-storage.service";
import { CryptoHelperService } from "../services/crypto-helper.service";
import { _AES_EncryptionService } from "../services/aes-encryption.service";

export const USE_AUTH_HEADER = "X-Use-Auth-Header";
const ENCRYPT_HEADER = "X-Encrypt";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private globalService: GlobalService,
    private localStorageService: LocalStorageService,
    private toastrMessageService: ToastrMessageService,
    private cryptoHelper: CryptoHelperService,
    private aes: _AES_EncryptionService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isLoginUrl = request.url.includes("user/login");

    // Read and strip encryption flag header
    const shouldEncrypt = request.headers.get(ENCRYPT_HEADER) === "true";
    if (shouldEncrypt) {
      request = request.clone({
        headers: request.headers.delete(ENCRYPT_HEADER),
      });
    }

    // Attach Bearer token for all non-public endpoints
    const isPublicUrl = /login/gi.test(request.url);
    if (!isPublicUrl) {
      const token =
        this.localStorageService.getLocalStorageItem("flexYear-token");
      request = request.clone({
        headers: request.headers.set("Authorization", `Bearer ${token}`),
      });
    }

    // Encrypt request body
    if (isLoginUrl && request.body && typeof request.body === "object") {
      // AES encrypt login payload — CryptoJS already preloaded by this point
      request = request.clone({
        body: { data: this.aes.encrypt(JSON.stringify(request.body)) },
      });
    } else if (shouldEncrypt && request.body !== null) {
      request = request.clone({
        body: this.cryptoHelper.encodePayload(request.body),
      });
    }

    return next.handle(request).pipe(
      map((evt: HttpEvent<any>) => {
        if (!(evt instanceof HttpResponse)) return evt;

        let body = evt.body;

        if (shouldEncrypt && !isLoginUrl) {
          body = this.cryptoHelper.decodePayload(body);
        }

        // Decode Base64 data field globally — handles login response
        body = this.tryDecodeBase64Data(body);

        return evt.clone({ body });
      }),

      tap((evt: HttpEvent<any>) => {
        if (evt instanceof HttpResponse) {
          if (
            evt.body?.status === "failure" &&
            evt.body?.response === "Invalid Access Token"
          ) {
            this.globalService.logout();
          }
        }
      }),

      catchError((err: any) => {
        if (err instanceof HttpErrorResponse && err.status === 500) {
          this.toastrMessageService.showError(
            "Server is not available. Please try again later!!",
          );
        }
        return of(err);
      }),
    );
  }

  private tryDecodeBase64Data(body: any): any {
    if (
      !body ||
      typeof body !== "object" ||
      typeof body.data !== "string" ||
      body.data.trim() === ""
    ) {
      return body;
    }

    try {
      const decoded = atob(body.data);
      const parsed = JSON.parse(decoded);
      return { ...body, ...parsed };
    } catch {
      return body;
    }
  }
}
